import { useState, useEffect, useCallback, useMemo } from "react"
import { useParams, Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import { NotFoundPage } from "@/pages/not-found"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Slider } from "@/components/ui/slider"
import { Loader2, Wallet, User, MapPin, Globe, ExternalLink, ArrowDown, ShieldCheck, ShieldX, Heart, Copy, Share, Check, PlayCircle } from "lucide-react"
import { VideoEmbedDialog } from "@/components/VideoEmbed"
import { parseVideoUrl, PLATFORM_LABELS } from "@/lib/videoEmbed"
import { Switch } from "@/components/ui/switch"
import { Pagination } from "@/components/ui/pagination"
import { useCursorPagination } from "@/hooks/useCursorPagination"
import * as jdenticon from "jdenticon"
import stwelephantLogo from "@/assets/tokens/stwelephant.svg"
import bnbLogo from "@/assets/tokens/bnb.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { useTransactionToast } from "@/hooks/useTransactionToast"
import { GAS_LIMIT, createSwapContract } from "@/lib/contracts"
import { ToggleableAmount } from "@/components/ui/toggleable-amount"
import { formatUnits, parseUnits, Contract, Signer, isAddress } from "ethers"
import { isUserRejection, getWalletErrorMessage } from "@/lib/walletErrors"
import { validateBalance, isAmountExceedingBalance } from "@/lib/validateBalance"
import { cn } from "@/lib/utils"
import { WalletConnectButton } from "@/components/wallet-connect-button"
import { PublicMessages } from "@/components/PublicMessages"

// ABIs
const STREAM_VAULT_ABI = [
  "function donateFor(address beneficiary, address referrer, string message, bool showSupport) payable external",
  "function donateForName(string vanityName, address referrer, string message, bool showSupport) payable external",
  "function messageService() external view returns (address)",
  "function getStream(address user) external view returns (tuple(uint256 earnedStWelephant, uint256 claimedStWelephant))",
  "function referrers(address) external view returns (address)",
  "function hasDonated(address) external view returns (bool)",
  "function lifetimeReceived(address) external view returns (uint256)",
  "function lifetimeAffiliateFees(address) external view returns (uint256)",
  "function lifetimeClaps(address) external view returns (uint256)",
  "function lifetimeDonated(address) external view returns (uint256)",
  "function totalClaps() external view returns (uint256)",
  "function totalMessages() external view returns (uint256)",
  "function estimateStWelephantForBNB(address beneficiary, uint256 bnbAmount) external view returns (uint256 netStWelephant, uint256 netWelephant)",
] as const

const VANITY_REGISTRY_ABI = [
  "function resolve(string name) external view returns (address)",
  "function getProfile(string name) external view returns (tuple(string bio, string location, string website, string displayName, string profilePic, string coverImage, string pageTitle, string message, string videoUrl))",
  "function getVanity(string name) external view returns (tuple(address owner, address referrer, uint256 resalePrice, uint256 purchasePrice, uint8 category))",
  "function tagRegistry() external view returns (address)",
  "function getPreferredVanity(address addr) external view returns (string)",
  "function referrerDeposits(address) external view returns (uint256)",
  "function isVerified(string name) external view returns (bool)",
  "function setVerified(string name, bool status) external",
  "function EDITOR_ROLE() external view returns (bytes32)",
  "function SUPERVISOR_ROLE() external view returns (bytes32)",
  "function hasRole(bytes32 role, address account) external view returns (bool)",
  "function getSupportedStreams(address depositor, uint256 cursor, uint256 limit) external view returns (string[] names, address[] owners, uint256 nextCursor)",
  "function getSupportedStreamCount(address depositor) external view returns (uint256)",
  "function getGoals(string name) external view returns (uint256[] usdAmounts, string[] descriptions)",
  "function getNameUsdReceived(string name) external view returns (uint256)",
] as const

import { BNB_DECIMALS, formatTokenAmount, formatUsdBigint, stWelToUsdViaRedeem } from "@/lib/ethersUtils"
import { CATEGORY_BY_ID } from "@/lib/categories"
import { STAKING_VAULT_ADDRESS } from "@/lib/contracts"
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000"

const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS
const VANITY_REGISTRY_ADDRESS = import.meta.env.VITE_VANITY_REGISTRY_ADDRESS
const MIN_DONATION_AMOUNT = parseUnits("0.01", BNB_DECIMALS) // Must match contract MIN_DONATION_AMOUNT

function fmtCompact(value: number): string {
  if (value === 0) return "0"
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`
  if (value < 0.001 && value > 0) return "<0.001"
  return value.toFixed(3).replace(/\.?0+$/, "")
}

const fmtBnb = (value: bigint, decimals: number) =>
  fmtCompact(parseFloat(formatUnits(value, decimals)))

const fmtStWel = (value: bigint) =>
  fmtCompact(parseFloat(formatUnits(value, 18)))

interface Profile {
  ownerAddress: string
  vanityName: string | null
  displayName: string
  bio: string
  location: string
  website: string
  profilePic: string
  coverImage: string
  pageTitle: string
  message: string
  videoUrl: string
  lifetimeReceived: bigint
  lifetimeAffiliateFees: bigint
  lifetimeDonated: bigint
  lifetimeClaps: bigint
  category: number
  tags: string[]
}

export function PublicStreamPage() {
  const { identifier } = useParams<{ identifier: string }>()
  const { provider, signer, address, isConnected, providerReady } = useEthersContext()
  const { showTxPending, showTxError } = useTransactionToast()

  const [profile, setProfile] = useState<Profile | null>(null)
  const [videoViewerOpen, setVideoViewerOpen] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null)
  const [messageServiceAddress, setMessageServiceAddress] = useState<string | null>(null)

  // Deposit form
  const [depositAmount, setDepositAmount] = useState("")
  const [depositMessage, setDepositMessage] = useState("")
  const [isDepositing, setIsDepositing] = useState(false)
  const [bnbBalance, setBnbBalance] = useState<bigint>(0n)
  const [amountPercent, setAmountPercent] = useState(0)

  // USD
  const [inputUsd, setInputUsd] = useState("$0.00")
  const [bnbBalanceUsd, setBnbBalanceUsd] = useState<string | null>(null)


  // stWELEPHANT output estimation
  const [outputAmount, setOutputAmount] = useState("")
  const [outputUsd, setOutputUsd] = useState("$0.00")
  const [isEstimating, setIsEstimating] = useState(false)
  const [welephantPriceUsd, setWelephantPriceUsd] = useState<bigint>(0n)

  // Pre-computed stWELEPHANT USD values (via previewRedeem)
  const [donatedUsd, setDonatedUsd] = useState<string | null>(null)
  const [receivedUsd, setReceivedUsd] = useState<string | null>(null)

  // Admin / verification
  const [isEditor, setIsEditor] = useState(false)
  const [isSupervisor, setIsSupervisor] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [isSettingVerified, setIsSettingVerified] = useState(false)

  // Support toggle (always defaults to true in the deposit form)
  const [showSupport, setShowSupport] = useState(true)
  const [copied, setCopied] = useState(false)

  // Goals
  const [goals, setGoals] = useState<{ usdAmount: bigint; description: string }[]>([])
  const [nameUsdReceived, setNameUsdReceived] = useState<bigint>(0n)

  // Supported streams tab
  interface SupportedEntry { name: string; owner: string; displayName: string; profilePic: string }
  const [supportedTotalCount, setSupportedTotalCount] = useState<number | undefined>(undefined)

  const depositAmountParsed = depositAmount ? parseUnits(depositAmount || "0", BNB_DECIMALS) : 0n

  const vaultContract = useMemo(() => {
    if (!provider || !STREAM_VAULT_ADDRESS) return null
    return new Contract(STREAM_VAULT_ADDRESS, STREAM_VAULT_ABI, provider)
  }, [provider])

  const vanityContract = useMemo(() => {
    if (!provider || !VANITY_REGISTRY_ADDRESS) return null
    return new Contract(VANITY_REGISTRY_ADDRESS, VANITY_REGISTRY_ABI, provider)
  }, [provider])

  const swapContract = useMemo(() => {
    if (!provider) return null
    return createSwapContract(provider)
  }, [provider])

  // Check admin roles and verified status
  useEffect(() => {
    if (!vanityContract || !profile?.vanityName) return
    vanityContract.isVerified(profile.vanityName)
      .then((v: boolean) => setIsVerified(v))
      .catch(() => setIsVerified(false))
  }, [vanityContract, profile?.vanityName])

  useEffect(() => {
    if (!vanityContract || !address) { setIsEditor(false); setIsSupervisor(false); return }
    Promise.all([
      vanityContract.EDITOR_ROLE(),
      vanityContract.SUPERVISOR_ROLE(),
    ]).then(async ([editorRole, supervisorRole]: [string, string]) => {
      const [hasEditor, hasSupervisor] = await Promise.all([
        vanityContract.hasRole(editorRole, address),
        vanityContract.hasRole(supervisorRole, address),
      ])
      setIsEditor(hasEditor)
      setIsSupervisor(hasSupervisor)
    }).catch(() => { setIsEditor(false); setIsSupervisor(false) })
  }, [vanityContract, address])

  // Resolve supported stream entries with profile data
  const resolveSupportedProfiles = useCallback(async (
    names: string[], owners: string[]
  ): Promise<SupportedEntry[]> => {
    if (!vanityContract) return names.map((n, i) => ({ name: n, owner: owners[i], displayName: "", profilePic: "" }))
    const entries: SupportedEntry[] = []
    for (let i = 0; i < names.length; i++) {
      try {
        const p = await vanityContract.getProfile(names[i])
        entries.push({ name: names[i], owner: owners[i], displayName: p.displayName || "", profilePic: p.profilePic || "" })
      } catch {
        entries.push({ name: names[i], owner: owners[i], displayName: "", profilePic: "" })
      }
    }
    return entries
  }, [vanityContract])

  // Load supported streams count
  useEffect(() => {
    if (!vanityContract || !resolvedAddress) return
    vanityContract.getSupportedStreamCount(resolvedAddress)
      .then((count: bigint) => setSupportedTotalCount(Number(count)))
      .catch(() => setSupportedTotalCount(0))
  }, [vanityContract, resolvedAddress])

  // Supported streams pagination
  const fetchSupportedPage = useCallback(async (cursor: bigint) => {
    if (!vanityContract || !resolvedAddress) return { items: [] as SupportedEntry[], nextCursor: 0n }
    const [names, owners, nextCursor] = await vanityContract.getSupportedStreams(resolvedAddress, cursor, 12) as [string[], string[], bigint]
    const entries = await resolveSupportedProfiles(names, owners)
    return { items: entries, nextCursor }
  }, [vanityContract, resolvedAddress, resolveSupportedProfiles])

  const supported = useCursorPagination<SupportedEntry>({
    fetchPage: fetchSupportedPage,
    totalCount: supportedTotalCount,
    pageSize: 12,
  })

  // Fetch BNB price and WELEPHANT price
  useEffect(() => {
    if (!swapContract) return
    swapContract.getWelephantPriceInUSD()
      .then((price: bigint) => setWelephantPriceUsd(price))
      .catch(() => setWelephantPriceUsd(0n))
  }, [swapContract])

  // Compute stWELEPHANT → USD via previewRedeem when profile and price are available
  useEffect(() => {
    if (!provider || !profile || welephantPriceUsd === 0n || !STAKING_VAULT_ADDRESS) return
    const vaultContract = new Contract(STAKING_VAULT_ADDRESS, [
      "function previewRedeem(uint256 shares) view returns (uint256)",
    ], provider)
    const redeem = (shares: bigint) => vaultContract.previewRedeem(shares) as Promise<bigint>

    const received = profile.lifetimeReceived + profile.lifetimeAffiliateFees

    let cancelled = false
    Promise.all([
      stWelToUsdViaRedeem(profile.lifetimeDonated, welephantPriceUsd, redeem),
      stWelToUsdViaRedeem(received, welephantPriceUsd, redeem),
    ]).then(([donated, recv]) => {
      if (cancelled) return
      setDonatedUsd(donated)
      setReceivedUsd(recv)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [provider, profile, welephantPriceUsd])

  // Compute USD for input
  useEffect(() => {
    if (!swapContract || !depositAmountParsed || depositAmountParsed === 0n) {
      setInputUsd("$0.00"); return
    }
    swapContract.estimateUsdForETH(depositAmountParsed)
      .then((usd: bigint) => setInputUsd(`$${formatUsdBigint(usd)}`))
      .catch(() => setInputUsd("$0.00"))
  }, [swapContract, depositAmountParsed])

  // Estimate stWELEPHANT output via StreamVault (mirrors donateFor math)
  useEffect(() => {
    if (!vaultContract || !resolvedAddress || !depositAmountParsed || depositAmountParsed === 0n) {
      setOutputAmount(""); setOutputUsd("$0.00"); return
    }
    setIsEstimating(true)
    vaultContract.estimateStWelephantForBNB(resolvedAddress, depositAmountParsed)
      .then(([netStWelephant, netWelephant]: [bigint, bigint]) => {
        setOutputAmount(formatTokenAmount(netStWelephant, 18))
        if (welephantPriceUsd > 0n) {
          const usdValue = welephantPriceUsd * netWelephant / BigInt(10 ** 9)
          setOutputUsd(`$${formatUsdBigint(usdValue)}`)
        } else {
          setOutputUsd("$0.00")
        }
      })
      .catch(() => { setOutputAmount(""); setOutputUsd("$0.00") })
      .finally(() => setIsEstimating(false))
  }, [vaultContract, resolvedAddress, depositAmountParsed, welephantPriceUsd])

  // Wallet balance USD
  useEffect(() => {
    if (!swapContract || bnbBalance === 0n) { setBnbBalanceUsd(null); return }
    swapContract.estimateUsdForETH(bnbBalance)
      .then((usd: bigint) => setBnbBalanceUsd(formatUsdBigint(usd)))
      .catch(() => setBnbBalanceUsd(null))
  }, [swapContract, bnbBalance])

  // Resolve identifier and load profile
  useEffect(() => {
    if (!identifier || !vanityContract || !vaultContract) return
    setIsLoadingProfile(true)

    const load = async () => {
      try {
        let ownerAddr: string
        let vanityName: string | null = null

        if (isAddress(identifier)) {
          ownerAddr = identifier
          const preferred = await vanityContract.getPreferredVanity(identifier)
          vanityName = preferred || null
        } else {
          const resolved = await vanityContract.resolve(identifier)
          if (!resolved || resolved === ZERO_ADDRESS) {
            setNotFound(true)
            setIsLoadingProfile(false)
            return
          }
          ownerAddr = resolved
          vanityName = identifier
        }

        setResolvedAddress(ownerAddr)

        // Fetch profile and message service address in parallel
        const profileName = vanityName || await vanityContract.getPreferredVanity(ownerAddr) || null
        const promises: Promise<unknown>[] = [
          vaultContract.messageService(),
          vaultContract.lifetimeReceived(ownerAddr),
          vaultContract.lifetimeAffiliateFees(ownerAddr),
          vaultContract.lifetimeDonated(ownerAddr),
          vaultContract.lifetimeClaps(ownerAddr),
        ]
        if (profileName) {
          promises.push(vanityContract.getProfile(profileName))
          promises.push(vanityContract.getVanity(profileName))
        }

        // Fetch tags from TagRegistry (separate contract)
        let tagsData: string[] | undefined
        if (profileName) {
          try {
            const tagAddr = await vanityContract.tagRegistry() as string
            if (tagAddr && tagAddr !== ZERO_ADDRESS) {
              const { Contract: EthContract } = await import("ethers")
              const tagContract = new EthContract(tagAddr, ["function getTagsByName(string name) external view returns (string[])"], provider)
              tagsData = await tagContract.getTagsByName(profileName) as string[]
            }
          } catch { /* tags unavailable */ }
        }

        const results = await Promise.all(promises)
        const msgAddr = results[0] as string
        const ltStWel = results[1] as bigint
        const ltAffiliate = results[2] as bigint
        const ltDepositedStWel = results[3] as bigint
        const ltClaps = results[4] as bigint
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const profileData = results[5] as any
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vanityData = results[6] as any

        if (msgAddr && msgAddr !== ZERO_ADDRESS) {
          setMessageServiceAddress(msgAddr)
        }

        setProfile({
          ownerAddress: ownerAddr,
          vanityName: profileName,
          displayName: profileData?.displayName || (profileName ?? shortenAddress(ownerAddr)),
          bio: profileData?.bio || "",
          location: profileData?.location || "",
          website: profileData?.website || "",
          profilePic: profileData?.profilePic || "",
          coverImage: profileData?.coverImage || "",
          pageTitle: profileData?.pageTitle || "",
          message: profileData?.message || "",
          videoUrl: profileData?.videoUrl || "",
          lifetimeReceived: ltStWel,
          lifetimeAffiliateFees: ltAffiliate,
          lifetimeDonated: ltDepositedStWel,
          lifetimeClaps: ltClaps,
          category: vanityData ? Number(vanityData.category) : 0,
          tags: tagsData ? [...tagsData] : [],
        })
      } catch (err) {
        console.error("Failed to load profile:", err)
      } finally {
        setIsLoadingProfile(false)
      }
    }

    load()
  }, [identifier, vanityContract, vaultContract])

  // Fetch goals for the vanity name
  useEffect(() => {
    if (!vanityContract || !profile?.vanityName) {
      setGoals([])
      setNameUsdReceived(0n)
      return
    }
    const name = profile.vanityName
    Promise.all([
      vanityContract.getGoals(name),
      vanityContract.getNameUsdReceived(name),
    ]).then(([goalsResult, usdReceived]: [{ usdAmounts: bigint[]; descriptions: string[] }, bigint]) => {
      const g = goalsResult.usdAmounts.map((usdAmount: bigint, i: number) => ({
        usdAmount,
        description: goalsResult.descriptions[i],
      }))
      setGoals(g)
      setNameUsdReceived(usdReceived)
    }).catch(() => {
      setGoals([])
      setNameUsdReceived(0n)
    })
  }, [vanityContract, profile?.vanityName])

  // Fetch wallet balance
  useEffect(() => {
    if (!provider || !address) return
    provider.getBalance(address).then(setBnbBalance).catch(() => {})
  }, [provider, address])

  const handleAmountSliderChange = useCallback((percent: number) => {
    setAmountPercent(percent)
    if (bnbBalance === 0n) { setDepositAmount(""); return }
    setDepositAmount(formatUnits((bnbBalance * BigInt(percent)) / 100n, BNB_DECIMALS))
  }, [bnbBalance])

  const handleSupportToggle = (checked: boolean) => {
    setShowSupport(checked)
  }


  const handleDeposit = async () => {
    if (!vaultContract || !signer || !resolvedAddress || !depositAmount) return
    setIsDepositing(true)
    try {
      const amount = parseUnits(depositAmount, BNB_DECIMALS)
      if (amount < MIN_DONATION_AMOUNT) {
        showTxError("Fund Stream", "Minimum donation is 0.01 BNB")
        setIsDepositing(false)
        return
      }
      const validation = validateBalance({ balance: bnbBalance, value: amount })
      if (!validation.valid) {
        showTxError("Fund Stream", validation.error!)
        setIsDepositing(false)
        return
      }
      const connectedVault = vaultContract.connect(signer as Signer) as Contract
      const tx = profile?.vanityName
        ? await connectedVault.donateForName(
            profile.vanityName, ZERO_ADDRESS, depositMessage, showSupport,
            { gasLimit: GAS_LIMIT, value: amount }
          )
        : await connectedVault.donateFor(
            resolvedAddress, ZERO_ADDRESS, depositMessage, showSupport,
            { gasLimit: GAS_LIMIT, value: amount }
          )
      const toast = showTxPending("Fund Stream", tx.hash)
      await tx.wait()
      toast.success()
      setDepositAmount("")
      setDepositMessage("")
    } catch (err) {
      if (!isUserRejection(err)) {
        showTxError("Fund Stream", getWalletErrorMessage(err, "Donation failed") ?? "Donation failed")
      }
    } finally {
      setIsDepositing(false)
    }
  }

  const isSelf = address && resolvedAddress && address.toLowerCase() === resolvedAddress.toLowerCase()

  if (notFound) return <NotFoundPage />

  return (
    <>
      <SEO
        title={profile ? `${profile.displayName} | WELEPHANT Stream` : "Stream | WELEPHANT"}
        description={profile?.bio || "Fund a WELEPHANT stream"}
      />
      <div className="container py-16">
        {/* Cover Image */}
        {profile && (
          <div className="relative mx-auto max-w-3xl mb-4 rounded-lg overflow-hidden">
            <img src={profile.coverImage || generateCoverDataUrl(resolvedAddress || identifier || "")} alt="Cover" className="w-full aspect-[3/1] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            {profile.pageTitle && (
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <h1 className="text-xl md:text-2xl font-bold text-white drop-shadow-lg">{profile.pageTitle}</h1>
              </div>
            )}
          </div>
        )}

        <div className="mx-auto max-w-3xl grid grid-cols-1 md:grid-cols-[320px,1fr] gap-4">
          {/* LEFT: Profile + Messages */}
          <div className="space-y-4">
            {isLoadingProfile ? (
              <Card>
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-4">Loading profile...</p>
                </div>
              </Card>
            ) : profile ? (
              <>
                <Card className="overflow-hidden">
                  <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                    <div className="flex items-start gap-4">
                      <div className="h-16 w-16 rounded-full bg-muted flex-shrink-0 flex items-center justify-center overflow-hidden border-2 border-background shadow-md">
                        {profile.profilePic ? (
                          <img src={profile.profilePic} alt={profile.displayName} className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <h2 className="text-xl font-bold tracking-tight">{profile.displayName}</h2>
                        {profile.vanityName && profile.displayName !== profile.vanityName && (
                          <p className="text-sm text-muted-foreground font-mono">/{profile.vanityName}</p>
                        )}
                        <p className="text-xs text-muted-foreground font-mono">
                          {shortenAddress(profile.ownerAddress)}
                        </p>
                      </div>
                    </div>

                    {profile.pageTitle && !profile.coverImage && (
                      <p className="text-base font-medium text-foreground mt-3">{profile.pageTitle}</p>
                    )}
                    {profile.bio && (
                      <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{profile.bio}</p>
                    )}
                    {(profile.location || profile.website) && (
                      <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
                        {profile.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />{profile.location}
                          </span>
                        )}
                        {profile.website && (
                          <a
                            href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Globe className="h-3 w-3" />
                            {profile.website.replace(/^https?:\/\//, "")}
                            <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Category + Share */}
                  <div className="px-6 py-2 border-t flex items-center justify-between">
                    <div>
                      {profile.category > 0 && CATEGORY_BY_ID.get(profile.category) && (
                        <Link to={`/streams/${CATEGORY_BY_ID.get(profile.category)!.slug}`} className="text-[11px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full hover:bg-primary/20 transition-colors">
                          {CATEGORY_BY_ID.get(profile.category)!.label}
                        </Link>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href)
                          setCopied(true)
                          setTimeout(() => setCopied(false), 2000)
                        }}
                      >
                        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({ title: profile.displayName, url: window.location.href })
                          } else {
                            navigator.clipboard.writeText(window.location.href)
                            setCopied(true)
                            setTimeout(() => setCopied(false), 2000)
                          }
                        }}
                      >
                        <Share className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Tags */}
                  {profile.tags.length > 0 && (
                    <div className="px-6 py-2 border-t flex flex-wrap gap-1.5">
                      {profile.tags.map((tag, idx) => (
                        <Link key={idx} to={`/trending/tag/${tag}`} className="text-[11px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full hover:bg-blue-500/20 transition-colors">
                          {tag}
                        </Link>
                      ))}
                    </div>
                  )}

                  <div className="px-6 py-3 border-t bg-muted/30 space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Donated</span>
                      <span className="font-semibold flex items-center gap-1">
                        <img src={stwelephantLogo} alt="" className="h-3 w-3" />
                        <ToggleableAmount amount={fmtStWel(profile.lifetimeDonated)} symbol="stWELEPHANT" hideLabel usdValue={donatedUsd} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Raised</span>
                      <span className="font-semibold flex items-center gap-1">
                        <img src={stwelephantLogo} alt="" className="h-3 w-3" />
                        <ToggleableAmount amount={fmtStWel(profile.lifetimeReceived + profile.lifetimeAffiliateFees)} symbol="stWELEPHANT" hideLabel usdValue={receivedUsd} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Likes</span>
                      <span className="font-semibold">{fmtCompact(Number(profile.lifetimeClaps))}</span>
                    </div>
                  </div>
                </Card>

                {/* Goals */}
                {goals.length > 0 && (
                  <Card>
                    <CardContent className="p-4 space-y-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Goals</p>
                      {goals.map((goal, idx) => {
                        const targetUsd = parseFloat(formatUnits(goal.usdAmount, 18))
                        const raisedUsd = parseFloat(formatUnits(nameUsdReceived, 18))
                        const pct = targetUsd > 0 ? Math.min(100, (raisedUsd / targetUsd) * 100) : 0
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium">{goal.description}</span>
                              <span className="text-muted-foreground text-xs">
                                ${raisedUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} / ${targetUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all bg-primary"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </CardContent>
                  </Card>
                )}

                {(profile.message || (profile.videoUrl && parseVideoUrl(profile.videoUrl))) && (
                  <div className="flex items-start gap-2">
                    {profile.message && (
                      <blockquote className="flex-1 border-l-4 border-primary/30 pl-4 py-2 text-sm italic text-foreground/80">
                        {profile.message}
                      </blockquote>
                    )}
                    {profile.videoUrl && parseVideoUrl(profile.videoUrl) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0"
                        title={`Play ${PLATFORM_LABELS[parseVideoUrl(profile.videoUrl)!.platform]} video`}
                        onClick={() => setVideoViewerOpen(true)}
                      >
                        <PlayCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}

                <VideoEmbedDialog
                  url={profile.videoUrl || null}
                  open={videoViewerOpen}
                  onOpenChange={setVideoViewerOpen}
                />


                {/* Admin Card — visible to EDITOR or SUPERVISOR */}
                {(isEditor || isSupervisor) && profile.vanityName && (
                  <Card>
                    <CardContent className="p-4 space-y-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Admin</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isVerified ? (
                            <ShieldCheck className="h-4 w-4 text-green-600" />
                          ) : (
                            <ShieldX className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">
                            {isVerified ? "Verified" : "Not verified"}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {!isVerified && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isSettingVerified}
                              onClick={async () => {
                                if (!signer || !profile.vanityName) return
                                setIsSettingVerified(true)
                                try {
                                  const vr = new Contract(VANITY_REGISTRY_ADDRESS, VANITY_REGISTRY_ABI, signer)
                                  const tx = await vr.setVerified(profile.vanityName, true)
                                  showTxPending("Verify Stream", tx.hash)
                                  await tx.wait()
                                  setIsVerified(true)
                                } catch (err: any) {
                                  if (!isUserRejection(err)) showTxError("Verify Stream", getWalletErrorMessage(err) || "Transaction failed")
                                } finally {
                                  setIsSettingVerified(false)
                                }
                              }}
                            >
                              {isSettingVerified ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify"}
                            </Button>
                          )}
                          {isVerified && isSupervisor && (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={isSettingVerified}
                              onClick={async () => {
                                if (!signer || !profile.vanityName) return
                                setIsSettingVerified(true)
                                try {
                                  const vr = new Contract(VANITY_REGISTRY_ADDRESS, VANITY_REGISTRY_ABI, signer)
                                  const tx = await vr.setVerified(profile.vanityName, false)
                                  showTxPending("Unverify Stream", tx.hash)
                                  await tx.wait()
                                  setIsVerified(false)
                                } catch (err: any) {
                                  if (!isUserRejection(err)) showTxError("Unverify Stream", getWalletErrorMessage(err) || "Transaction failed")
                                } finally {
                                  setIsSettingVerified(false)
                                }
                              }}
                            >
                              {isSettingVerified ? <Loader2 className="h-3 w-3 animate-spin" /> : "Unverify"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

              </>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">Profile not found</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT: Donate / Messages */}
          <div>
            <Card>
              <Tabs defaultValue="donate">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="donate">Donate</TabsTrigger>
                  <TabsTrigger value="messages">Activity</TabsTrigger>
                  <TabsTrigger value="streams">Streams</TabsTrigger>
                </TabsList>

                <TabsContent value="donate" className="mt-0">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Donate to {profile?.displayName || profile?.vanityName || "this stream"}
                </p>
                {!providerReady ? (
                  null
                ) : !isConnected ? (
                  <div className="text-center py-12">
                    <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">Connect your wallet to fund this stream</p>
                    <div className="flex justify-center">
                      <WalletConnectButton />
                    </div>
                  </div>
                ) : isSelf ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground text-sm">This is your own stream. Go to <a href="/stream" className="text-primary hover:underline">/stream</a> to manage it.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* BNB Input */}
                    <div className={cn("rounded-xl bg-muted/50 p-4", (isAmountExceedingBalance(depositAmount, bnbBalance, BNB_DECIMALS) || (depositAmountParsed > 0n && depositAmountParsed < MIN_DONATION_AMOUNT)) && "bg-destructive/10 border border-destructive/30")}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-muted-foreground">Donate</span>
                        <img src={bnbLogo} alt="BNB" className="w-6 h-6" />
                        <span className="font-medium">BNB</span>
                      </div>

                      <div className="flex items-center justify-end gap-2 mb-3">
                        <Slider
                          value={[amountPercent]}
                          onValueChange={([value]) => handleAmountSliderChange(value)}
                          min={0} max={100} step={1}
                          className="w-24"
                        />
                        <span className="text-sm text-primary w-10 text-right">{amountPercent}%</span>
                      </div>

                      <div className="mb-2">
                        <Input
                          type="number"
                          placeholder="0"
                          value={depositAmount}
                          onChange={(e) => setDepositAmount(e.target.value)}
                          className="text-2xl !border-0 bg-transparent p-0 h-auto text-right"
                        />
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => setDepositAmount(formatUnits(bnbBalance, BNB_DECIMALS))}
                          className={cn("flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors", depositAmountParsed > bnbBalance && depositAmountParsed > 0n && "text-destructive")}
                        >
                          <Wallet className="h-3 w-3" />
                          <ToggleableAmount amount={fmtBnb(bnbBalance, BNB_DECIMALS)} symbol="BNB" usdValue={bnbBalanceUsd} />
                        </button>
                        <span className="text-sm text-muted-foreground">{inputUsd}</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-center -my-1 relative z-10">
                      <div className="rounded-full border-4 border-background bg-muted p-2">
                        <ArrowDown className="h-4 w-4" />
                      </div>
                    </div>

                    {/* Output preview */}
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm text-muted-foreground">To</span>
                        <img src={stwelephantLogo} alt="stWELEPHANT" className="w-6 h-6" />
                        <span className="font-medium">stWELEPHANT</span>
                      </div>

                      <div className={cn(
                        "text-2xl font-medium mb-2 text-right",
                        isEstimating && "animate-pulse"
                      )}>
                        {isEstimating ? (
                          <Loader2 className="h-6 w-6 animate-spin ml-auto" />
                        ) : (
                          outputAmount || "0"
                        )}
                      </div>

                      <div className="flex items-center justify-end">
                        <span className="text-sm text-muted-foreground">{outputUsd}</span>
                      </div>
                    </div>

                    {/* Support toggle + Optional Message */}
                    <div className="rounded-xl bg-muted/50 p-4">
                      {profile?.vanityName && isConnected && (
                        <div className="flex items-center justify-between mb-3 pb-3 border-b">
                          <label htmlFor="show-support" className="text-sm text-muted-foreground">
                            Show support for {profile.displayName || profile.vanityName}
                          </label>
                          <Switch
                            id="show-support"
                            checked={showSupport}
                            onCheckedChange={handleSupportToggle}
                          />
                        </div>
                      )}
                      <span className="text-sm text-muted-foreground mb-2 block">Message (optional)</span>
                      <Textarea
                        value={depositMessage}
                        onChange={(e) => setDepositMessage(e.target.value.slice(0, 200))}
                        placeholder="Leave a message with your donation..."
                        rows={2}
                        className="!border-0 bg-transparent p-0 text-sm resize-none"
                      />
                      {depositMessage && (
                        <span className="text-[10px] text-muted-foreground">{depositMessage.length}/200</span>
                      )}
                    </div>

                    {/* Donate Button */}
                    <Button
                      onClick={handleDeposit}
                      disabled={isDepositing || !depositAmount || depositAmountParsed === 0n || depositAmountParsed < MIN_DONATION_AMOUNT || depositAmountParsed > bnbBalance}
                      className="w-full h-14 text-lg"
                    >
                      {isDepositing ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Funding...</>
                      ) : (
                        "Fund"
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
                </TabsContent>

                <TabsContent value="messages" className="mt-0">
                  <CardContent className="p-4">
                    {resolvedAddress ? (
                      <>
                        <p className="text-sm text-muted-foreground mb-3">
                          Activity for {profile?.displayName || profile?.vanityName || "this stream"}
                        </p>
                        <PublicMessages messageServiceAddress={messageServiceAddress} ownerAddress={resolvedAddress} vanityRegistryAddress={VANITY_REGISTRY_ADDRESS} />
                      </>
                    ) : null}
                  </CardContent>
                </TabsContent>

                <TabsContent value="streams" className="mt-0">
                  <CardContent className="p-4">
                    {supported.loading && supported.items.length === 0 ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : supported.items.length > 0 ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Streams {profile?.displayName || profile?.vanityName || "this stream"} supports
                        </p>
                        {supported.items.map((entry) => (
                          <a
                            key={entry.name}
                            href={`/s/${entry.name}`}
                            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                          >
                            {entry.profilePic ? (
                              <img src={entry.profilePic} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="h-8 w-8 rounded-full overflow-hidden shrink-0" dangerouslySetInnerHTML={{ __html: jdenticon.toSvg(entry.owner, 32) }} />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{entry.displayName || entry.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{entry.owner.slice(0, 6)}...{entry.owner.slice(-4)}</p>
                            </div>
                          </a>
                        ))}
                        <Pagination
                          currentPage={supported.currentPage}
                          totalPages={supported.totalPages}
                          approximate
                          hasPrev={supported.hasPrev}
                          hasNext={supported.hasNext}
                          onPrev={supported.goPrev}
                          onNext={supported.goNext}
                          loading={supported.loading}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Heart className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground">No supported streams yet</p>
                      </div>
                    )}
                  </CardContent>
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}

function generateCoverDataUrl(seed: string): string {
  const svg = jdenticon.toSvg(`${seed}-cover`, 360)
  const cropped = svg
    .replace('height="360"', 'height="120"')
    .replace('viewBox="0 0 360 360"', 'viewBox="0 120 360 120"')
  return `data:image/svg+xml;base64,${btoa(cropped)}`
}

function shortenAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

